import { Button, Card, Form, Input, Segmented, Space, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationSessionSelect,
  EducationStudentSelect,
} from '@/pages/education/components/EducationLookupSelects';
import ReportDocumentPanel from '@/components/finance/report-document/ReportDocumentPanel';
import type { ReportParams } from '@/services/reportDocuments.service';
import { useAuthStore } from '@/store/authStore';

const { Paragraph, Text } = Typography;

type DocKind = 'tc' | 'fee' | 'progress' | 'report_card';

const DOC_META: Record<
  DocKind,
  {
    reportKey: string;
    sendPermissions: string[];
  }
> = {
  tc: {
    reportKey: 'education-transfer-certificate',
    sendPermissions: ['education.transfer_certificate.issue', 'education.admin'],
  },
  fee: {
    reportKey: 'education-fee-receipt',
    sendPermissions: ['education.fee.collect', 'education.fee.manage', 'education.admin'],
  },
  progress: {
    reportKey: 'education-student-progress',
    sendPermissions: ['education.student.view', 'education.enrollment.view', 'education.admin'],
  },
  report_card: {
    reportKey: 'education-report-card',
    sendPermissions: ['education.assessment.view', 'education.assessment.publish', 'education.admin'],
  },
};

function kindFromQuery(raw: string | null): DocKind {
  if (raw === 'fee' || raw === 'progress' || raw === 'report_card' || raw === 'tc') return raw;
  return 'tc';
}

export default function PrintHubPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const perms = user?.permissions || [];
  const isAdmin = Boolean(user?.is_super_admin) || perms.includes('education.admin');

  const canTc =
    isAdmin ||
    perms.includes('education.transfer_certificate.issue') ||
    perms.includes('education.enrollment.view');
  const canFee =
    isAdmin ||
    perms.includes('education.fee.view') ||
    perms.includes('education.fee.collect') ||
    perms.includes('education.fee.manage');
  const canProgress =
    isAdmin ||
    perms.includes('education.student.view') ||
    perms.includes('education.student.view_all') ||
    perms.includes('education.enrollment.view') ||
    perms.includes('education.attendance.view');
  const canReportCard =
    isAdmin ||
    perms.includes('education.assessment.view') ||
    perms.includes('education.assessment.entry') ||
    perms.includes('education.assessment.publish');

  const availableKinds = useMemo(() => {
    const kinds: DocKind[] = [];
    if (canTc) kinds.push('tc');
    if (canFee) kinds.push('fee');
    if (canProgress) kinds.push('progress');
    if (canReportCard) kinds.push('report_card');
    return kinds;
  }, [canTc, canFee, canProgress, canReportCard]);

  const initialKind = kindFromQuery(searchParams.get('doc'));
  const [kind, setKind] = useState<DocKind>(
    availableKinds.includes(initialKind) ? initialKind : availableKinds[0] || 'tc'
  );
  const [draft, setDraft] = useState<Record<string, string>>({
    id: searchParams.get('id') || '',
    student_id: searchParams.get('student_id') || '',
    session_id: searchParams.get('session_id') || '',
    scheme_id: searchParams.get('scheme_id') || '',
  });
  const [params, setParams] = useState<ReportParams | null>(() => {
    const id = searchParams.get('id');
    const studentId = searchParams.get('student_id');
    const sessionId = searchParams.get('session_id') || undefined;
    const schemeId = searchParams.get('scheme_id') || undefined;
    const doc = kindFromQuery(searchParams.get('doc'));
    if ((doc === 'tc' || doc === 'fee') && id) return { id };
    if ((doc === 'progress' || doc === 'report_card') && studentId) {
      return {
        student_id: studentId,
        ...(sessionId ? { session_id: sessionId } : {}),
        ...(doc === 'report_card' && schemeId ? { scheme_id: schemeId } : {}),
      };
    }
    return null;
  });

  const meta = DOC_META[kind];

  const applyKind = (next: DocKind) => {
    setKind(next);
    setParams(null);
    const q = new URLSearchParams();
    q.set('doc', next);
    setSearchParams(q, { replace: true });
  };

  const generate = (values: Record<string, string | undefined>) => {
    const nextDraft = {
      id: String(values.id || '').trim(),
      student_id: String(values.student_id || '').trim(),
      session_id: String(values.session_id || '').trim(),
      scheme_id: String(values.scheme_id || '').trim(),
    };
    setDraft(nextDraft);
    const q = new URLSearchParams();
    q.set('doc', kind);
    let nextParams: ReportParams | null = null;
    if (kind === 'tc' || kind === 'fee') {
      if (!nextDraft.id) return;
      nextParams = { id: nextDraft.id };
      q.set('id', nextDraft.id);
    } else {
      if (!nextDraft.student_id) return;
      nextParams = { student_id: nextDraft.student_id };
      q.set('student_id', nextDraft.student_id);
      if (nextDraft.session_id) {
        nextParams.session_id = nextDraft.session_id;
        q.set('session_id', nextDraft.session_id);
      }
      if (kind === 'report_card' && nextDraft.scheme_id) {
        nextParams.scheme_id = nextDraft.scheme_id;
        q.set('scheme_id', nextDraft.scheme_id);
      }
    }
    setParams(nextParams);
    setSearchParams(q, { replace: true });
  };

  const kindLabel = (value: DocKind) => {
    if (value === 'tc') return t('transferCertificate');
    if (value === 'fee') return t('feeReceipt');
    if (value === 'progress') return t('studentProgressSheet');
    return t('reportCard');
  };

  const emptyHint = (() => {
    if (kind === 'tc') return t('printSelectCertificate', { defaultValue: 'Enter a transfer certificate id and generate the document.' });
    if (kind === 'fee') return t('printSelectDemand', { defaultValue: 'Enter a fee demand id and generate the receipt.' });
    if (kind === 'progress') {
      return t('printSelectStudentProgress', { defaultValue: 'Select a student and generate the progress sheet.' });
    }
    return t('printSelectReportCard', { defaultValue: 'Select a student and generate the report card.' });
  })();

  return (
    <EducationPageChrome
      title={t('printHub')}
      description={t('printHubIntro')}
      extra={<Button onClick={() => navigate('/education')}>{t('backToHub', { defaultValue: 'Back' })}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Paragraph type="secondary">{t('printHubScopeNote')}</Paragraph>

        {!availableKinds.length ? (
          <Card size="small">
            <Text type="secondary">{t('printHubNoAccess')}</Text>
          </Card>
        ) : (
          <>
            <Segmented
              value={kind}
              onChange={(value) => applyKind(value as DocKind)}
              options={availableKinds.map((item) => ({ value: item, label: kindLabel(item) }))}
            />

            <ReportDocumentPanel
              reportKey={meta.reportKey}
              params={params}
              allowPreprinted
              sendPermissions={meta.sendPermissions}
              documentId={typeof params?.id === 'string' ? params.id : undefined}
              emailLabel={t('email', { defaultValue: 'Email' })}
              emptyHint={emptyHint}
              filters={
                <Form
                  layout="inline"
                  key={kind}
                  initialValues={draft}
                  onFinish={(values) => generate(values)}
                  style={{ width: '100%' }}
                >
                  {kind === 'tc' || kind === 'fee' ? (
                    <Form.Item
                      name="id"
                      label={kind === 'tc' ? t('certificateId') : t('demandId')}
                      rules={[{ required: true }]}
                    >
                      <Input
                        style={{ width: 280 }}
                        placeholder={kind === 'tc' ? t('certificateIdPlaceholder') : t('demandIdPlaceholder')}
                      />
                    </Form.Item>
                  ) : (
                    <>
                      <Form.Item name="student_id" label={t('student')} rules={[{ required: true }]}>
                        <EducationStudentSelect style={{ minWidth: 280 }} />
                      </Form.Item>
                      <Form.Item name="session_id" label={t('sessionOptional')}>
                        <EducationSessionSelect style={{ minWidth: 220 }} allowClear />
                      </Form.Item>
                      {kind === 'report_card' ? (
                        <Form.Item name="scheme_id" label={t('schemeOptional')}>
                          <Input style={{ width: 240 }} placeholder={t('schemeIdPlaceholder')} />
                        </Form.Item>
                      ) : null}
                    </>
                  )}
                  <Form.Item>
                    <Space>
                      <Button type="primary" htmlType="submit">
                        {t('generate', { defaultValue: 'Generate' })}
                      </Button>
                      {kind === 'tc' ? (
                        <Button onClick={() => navigate('/education/transfer-certificates')}>
                          {t('browseCertificates')}
                        </Button>
                      ) : null}
                      {kind === 'fee' ? (
                        <Button onClick={() => navigate('/education/fees/demands')}>{t('feeDemands')}</Button>
                      ) : null}
                      {kind === 'report_card' ? (
                        <Button onClick={() => navigate('/education/assessments')}>{t('browseAssessments')}</Button>
                      ) : null}
                    </Space>
                  </Form.Item>
                </Form>
              }
            />
            {kind === 'fee' ? <Text type="secondary">{t('printFeeReceiptHint')}</Text> : null}
            {kind === 'progress' ? <Text type="secondary">{t('printStudentProgressHint')}</Text> : null}
            {kind === 'report_card' ? <Text type="secondary">{t('printReportCardHint')}</Text> : null}
          </>
        )}
      </Space>
    </EducationPageChrome>
  );
}
